import { isArray } from '@ember/array';
import { get } from '@ember/object';
import { isEmpty } from '@ember/utils';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import moment from 'moment';
import {
  byKeys,
  findBy,
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export const FIRST_HALF = 'first';
export const SECOND_HALF = 'second';
export const ANY_HALF = 'any';

export function mapMilestonesToHalfYears(milestones) {
  if (!isArray(milestones)) {
    return [];
  }
  let sortedMilestones = sortBy(milestones, 'sequence', 'label');
  return sortedMilestones.reduce((map, milestone) => {
    if (milestone.type === MilestoneType.YEAR_LEVEL) {
      let yearLevelMilestones = milestones
        .filter((m) => {
          return (
            m.type === MilestoneType.YEAR_LEVEL &&
            m.yearLevel === milestone.yearLevel
          );
        })
        .sort(byKeys('sequence', 'label'));
      // If we have an even number of year-level milestones,
      // split them equally between the 2 year-halfs.
      if (yearLevelMilestones.length % 2 === 0) {
        let cutoff = yearLevelMilestones.length / 2;
        yearLevelMilestones.forEach((milestone, index) => {
          if (index < cutoff) {
            map[milestone.id] = FIRST_HALF;
          } else {
            map[milestone.id] = SECOND_HALF;
          }
        });
        return map;
      }
    }

    // Other types of milestone (or those with more than 2 per year level) should just split the calendar in half.
    // Since the due date on the progression must be checked to ensure it's for the relevant year, don't bother
    // to check which half of the year it is here - that can be done using the function below.
    map[milestone.id] = ANY_HALF;

    return map;
  }, {});
}

export function getProgressionForHalfYear(progressions, map, dates, half) {
  switch (half) {
    case FIRST_HALF:
      return progressions.find((progression) => {
        let milestoneHalfYear =
          get(map, progression.get('milestone.id')) ?? 'any';
        switch (milestoneHalfYear) {
          case FIRST_HALF:
            // Milestone is for the first half of the year.
            // Include this progression if it's in the correct calendar year.
            return moment(progression.dueDate).isBetween(
              dates.startDate,
              dates.endDate,
              'day',
              '[]',
            );
          case SECOND_HALF:
            // Milestone is for the second half of the year - ignore this progression.
            return false;
        }

        // Milestone could fall due in either half (not based on year level), or half-year not determined.
        // Fall back to checking if the progression's due date is in the first half of the calendar year.
        return moment(progression.dueDate).isBetween(
          dates.startDate,
          dates.middleDate,
          'day',
          '[]',
        );
      });

    case SECOND_HALF:
      return progressions.find((progression) => {
        let milestoneHalfYear =
          get(map, progression.get('milestone.id')) ?? 'any';
        switch (milestoneHalfYear) {
          case FIRST_HALF:
            // Milestone is for the first half of the year - ignore this progression.
            return false;
          case SECOND_HALF:
            // Milestone is for the second half of the year.
            // Include this progression if it's in the correct calendar year.
            return moment(progression.dueDate).isBetween(
              dates.startDate,
              dates.endDate,
              'day',
              '[]',
            );
        }

        // Milestone could fall due in either half (not based on year level), or half-year not determined.
        // Fall back to checking if the progression's due date is in the second half of the calendar year.
        return moment(progression.dueDate).isBetween(
          dates.middleDate,
          dates.endDate,
          'day',
          '(]',
        );
      });
  }

  return null;
}

export function getMilestoneSetOptions(milestoneSets, defaultMilestones) {
  // Sort Milestone Sets alphabetically.
  milestoneSets = [...milestoneSets].sort(byKeys('label'));

  // Add "default milestones" virtual option if there are any default
  // Milestones - or if there are no Milestone Sets.
  if (!isEmpty(defaultMilestones) || isEmpty(milestoneSets)) {
    milestoneSets.unshift({
      label: 'Default Milestones',
      isDefault: true,
    });
  }
  return milestoneSets;
}

// getDefaultMilestoneSet returns the Milestone Set to be selected by default
// when viewing multiple Subjects filtered by Milestone Set, based on user
// preference with a fallback based on the school's Milestone Sets.
export function getDefaultMilestoneSet(milestoneSets, userPreference) {
  let milestoneSet;
  if (!isEmpty(userPreference)) {
    if (userPreference === 'default') {
      // User has chosen the "Default Milestones" virtual Milestone Set.
      milestoneSet = findBy(milestoneSets, 'isDefault');
    } else {
      // User appears to have chosen a "real" Milestone Set.
      milestoneSet = findBy(milestoneSets, 'id', userPreference);
    }
    if (!isEmpty(milestoneSet)) {
      // We found a Milestone Set matching the user's preference.  Return it.
      return milestoneSet;
    }
  }

  // User has no saved preference, or there's no matching Milestone Set.
  // Attempt to return the most recently created Milestone Set.
  milestoneSet = rejectBy(milestoneSets, 'isDefault')
    .sort(byKeys('createTime'))
    .at(-1);
  if (isEmpty(milestoneSet)) {
    // No "real" Milestone Sets; try for "Default Milestones" instead.
    milestoneSet = findBy(milestoneSets, 'isDefault');
  }
  return milestoneSet;
}
